import { column, Css, GridColumn, GridDataRow, GridTable, numericColumn, simpleHeader } from "@homebound/beam";
import { useMemo } from "react";
import { emptyCellDash } from "src/components/gridTableCells";
import { PlanBasedLineItemsTab_LineItemFragment } from "src/routes/projects/commitments/components/PlanBasedLineItemsTab.generated";
import { ScopeLinesSection_LineItemFragment } from "./ScopeLinesSection.generated";

type ScopeLinesSectionProps = { lineItems: PlanBasedLineItemsTab_LineItemFragment[] };

export function ScopeLinesSection(props: ScopeLinesSectionProps) {
  const { lineItems } = props;
  const rows = useMemo<GridDataRow<ScopeLineRow>[]>(
    () => [
      simpleHeader,
      ...lineItems
        .filter((cli) => cli.primaryBidContractLineItem?.forCoveringTakeoffs)
        .map((cli) => ({ kind: "data" as const, id: cli.id, data: cli })),
    ],
    [lineItems],
  );
  return (
    <section data-testid="scopeLinesSection">
      <h2 css={Css.mdSb.mt3.mb1.$}>Scope Lines</h2>
      <GridTable columns={createScopeLinesColumns()} rows={rows} fallbackMessage="No scope lines" />
    </section>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ScopeLinesSection_LineItemFragment };
export type ScopeLineRow = HeaderRow | DataRow;

export function createScopeLinesColumns(renderInPdf?: boolean): GridColumn<ScopeLineRow>[] {
  return [
    column<ScopeLineRow>({ header: () => "Item Code", data: (cli) => cli.projectItem.item.fullCode }),
    column<ScopeLineRow>({ header: () => "Name", data: (cli) => cli.projectItem.displayName }),
    column<ScopeLineRow>({
      header: () => "Option",
      data: (cli) => cli.projectItem.itemTemplateItem?.options.map((o) => o.name).join(", ") || emptyCellDash,
    }),
    column<ScopeLineRow>({ header: () => "Allocation", data: (cli) => cli.projectItem.task?.name ?? emptyCellDash }),
    ...(renderInPdf
      ? []
      : [column<ScopeLineRow>({ header: () => "Internal Note", data: (cli) => cli.internalNote ?? emptyCellDash })]),
    column<ScopeLineRow>({ header: () => "Location", data: (cli) => cli.projectItem.location.displayLocationPath }),
    column<ScopeLineRow>({
      header: () => "Material Code",
      data: (cli) => cli.projectItem.materialVariant?.code ?? emptyCellDash,
    }),
    numericColumn<ScopeLineRow>({ header: () => "Quantity", data: (cli) => cli.quantity ?? emptyCellDash }),
    column<ScopeLineRow>({ header: () => "Unit", data: (cli) => cli.projectItem.unitOfMeasure.name }),
  ];
}
